import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSiteContext } from "../components/layout/Layout";
import {
  BellRing, MessageSquare, Mail, Plus, Trash2, Send, ChevronDown, ChevronUp, ArrowDown,
  AlertOctagon, AlertTriangle, CheckCircle2, UserRound, Phone, History, Building2, X,
} from "lucide-react";

// Alerts are raised with exactly these severities (alert_engine, gateway sweep, automation).
// "high"/"medium" existed in older contact records but nothing ever raises them.
const SEVERITIES = [
  { key: "critical", label: "Critical", on: "bg-red-600 text-white border-red-600", hint: "Pressure low, breaker tripped, no run feedback" },
  { key: "warning", label: "Warning", on: "bg-amber-500 text-white border-amber-500", hint: "Gateway offline, pressure/level high, automation unavailable" },
  { key: "info", label: "Info", on: "bg-sky-600 text-white border-sky-600", hint: "System restored — can be noisy" },
];
const LEGACY_SEVERITIES = ["high", "medium"];

// Common roles for alert recipients. Anything else is kept as free text via "Other…".
const ROLES = ["Owner / Management", "Facility Manager", "Site Manager", "Building Manager",
  "Fire Safety Officer", "Site Engineer", "Maintenance Technician", "Security Supervisor", "Admin"];

function RoleField({ value, onChange }) {
  const known = ROLES.includes(value);
  const [custom, setCustom] = useState(!!value && !known);
  return (
    <div className="space-y-1">
      <Select value={custom ? "__other__" : (value || "")} onValueChange={(v) => {
        if (v === "__other__") { setCustom(true); onChange(known ? "" : value); } else { setCustom(false); onChange(v); }
      }}>
        <SelectTrigger className="bg-gray-50 border-gray-200 h-8 mt-0.5 text-sm"><SelectValue placeholder="Select role" /></SelectTrigger>
        <SelectContent className="bg-white border-gray-200 text-gray-900">
          {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          <SelectItem value="__other__">Other…</SelectItem>
        </SelectContent>
      </Select>
      {custom && <Input autoFocus className="bg-gray-50 border-gray-200 h-8" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type the role" />}
    </div>
  );
}

const digits = (v) => (v || "").replace(/\D/g, "");
// Seeded demo contacts use numbers like +91 98200 11111 — five identical trailing digits.
const looksLikePlaceholder = (v) => /(\d)\1{4}$/.test(digits(v));

function contactIssues(c) {
  const issues = [];
  if (!c.is_active) return issues;
  if (!digits(c.whatsapp) && !(c.email || "").trim()) issues.push("No WhatsApp number or email — this contact will never receive alerts.");
  else if (!digits(c.whatsapp)) issues.push("No WhatsApp number — alerts only go by email.");
  if (digits(c.whatsapp) && looksLikePlaceholder(c.whatsapp)) issues.push(`${c.whatsapp} looks like a placeholder/demo number — real alerts would be sent to it.`);
  if (!SEVERITIES.some((s) => (c.severities || []).includes(s.key))) issues.push("No alert types selected — nothing will be sent.");
  return issues;
}

export default function AlertDispatch() {
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(null); // JSON of the last loaded/saved settings
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [log, setLog] = useState([]);
  const { user } = useAuth();
  const { selectedOrg, orgs, sites, selectedSite } = useSiteContext();
  // Settings are per organisation. Platform users edit the org picked in the header's tenant
  // selector; everyone else always edits their own org (the backend enforces the same).
  const isPlatform = user?.role === "super_admin" || user?.role === "platform_support";
  const orgParams = isPlatform && selectedOrg ? { org_id: selectedOrg } : {};
  const orgName = isPlatform ? (orgs || []).find((o) => o.org_id === selectedOrg)?.name : null;
  // Contacts can be tied to sites (empty site_ids = every site). With a site picked in the
  // header, show that site's contacts plus the "All sites" ones; "Show everyone" lifts that.
  const viewSite = selectedSite && selectedSite !== "all" ? selectedSite : null;
  const [showAll, setShowAll] = useState(false);
  const siteName = (id) => (sites || []).find((s) => s.site_id === id)?.name || id;
  const covers = (c, id) => !(c.site_ids || []).length || c.site_ids.includes(id);

  const load = () => {
    setSettings(null);
    api.get("/alert-dispatch/settings", { params: orgParams }).then((r) => { setSettings(r.data); setSaved(JSON.stringify(r.data)); });
    api.get("/alert-dispatch/log", { params: orgParams }).then((r) => setLog(r.data || [])).catch(() => setLog([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [selectedOrg]);

  const dirty = settings && saved !== JSON.stringify(settings);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/alert-dispatch/settings", settings, { params: orgParams });
      setSaved(JSON.stringify(settings));
      toast.success("Alert contacts saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const addContact = () => setSettings({
    ...settings,
    contacts: [...settings.contacts, {
      contact_id: `dc_${Math.random().toString(36).slice(2, 12)}`,
      name: "", role: "Site Engineer", phone: "", whatsapp: "", email: "",
      severities: ["critical", "warning"], is_active: true,
      site_ids: viewSite ? [viewSite] : [],   // added while viewing a site → that site
    }],
  });
  const updateContact = (idx, patch) => {
    const copy = [...settings.contacts];
    copy[idx] = { ...copy[idx], ...patch };
    setSettings({ ...settings, contacts: copy });
  };
  const deleteContact = (idx) => {
    const c = settings.contacts[idx];
    if (!window.confirm(`Remove ${c.name || "this contact"}? (Takes effect when you save.)`)) return;
    setSettings({ ...settings, contacts: settings.contacts.filter((_, i) => i !== idx) });
  };
  const toggleSeverity = (idx, sev) => {
    const c = settings.contacts[idx];
    const cur = c.severities || [];
    updateContact(idx, { severities: cur.includes(sev) ? cur.filter((s) => s !== sev) : [...cur, sev] });
  };

  const testAlert = async (contact, channel) => {
    try {
      await api.post("/alert-dispatch/test", { contact_id: contact.contact_id, channel }, { params: orgParams });
      toast.success(`Test sent to ${contact.name || "contact"} via ${channel === "whatsapp" ? "WhatsApp" : "email"}`);
      api.get("/alert-dispatch/log", { params: orgParams }).then((r) => setLog(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const summary = useMemo(() => {
    if (!settings) return null;
    const active = settings.contacts.filter((c) => c.is_active);
    const wa = active.filter((c) => digits(c.whatsapp) && (c.severities || []).includes("critical"));
    const issues = settings.contacts.flatMap((c, i) => contactIssues(c).map((msg) => ({ i, name: c.name || `Contact ${i + 1}`, msg })));
    return { active: active.length, waCritical: wa.length, issues };
  }, [settings]);

  if (!settings) return <div className="text-gray-500">Loading…</div>;

  const savedIds = new Set((JSON.parse(saved || "{}").contacts || []).map((c) => c.contact_id));
  // Keep each contact's index into settings.contacts — updateContact / deleteContact use it.
  const listed = settings.contacts.map((c, idx) => ({ c, idx }))
    .filter(({ c }) => showAll || !viewSite || covers(c, viewSite));
  const hidden = settings.contacts.length - listed.length;

  return (
    <div className="space-y-3" data-testid="alert-dispatch-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="w-9 h-9 rounded-lg bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center shrink-0">
            <BellRing className="w-5 h-5" strokeWidth={2} />
          </div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">Alert Dispatch</h1>
          <span className="text-[11px] text-gray-500">Who receives alerts on WhatsApp and email</span>
          {isPlatform && (
            <span className="text-[11px] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-orange-50 border border-orange-200 text-orange-800">
              Organisation: <b>{orgName || selectedOrg || "your own"}</b> — change it in the header
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">Unsaved changes</span>}
          <Button size="sm" onClick={save} disabled={busy || !dirty} className="f360-gradient border-0 text-white" data-testid="save-dispatch-btn">
            {busy ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </div>

      {/* Channels + at-a-glance status */}
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {[
          { key: "enable_whatsapp", label: "WhatsApp", icon: MessageSquare, tone: "from-emerald-500 to-green-600" },
          { key: "enable_email", label: "Email", icon: Mail, tone: "from-orange-500 to-pink-600" },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="glass rounded-lg px-3 py-3 flex items-center gap-3" data-testid={`channel-${c.key}`}>
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.tone} flex items-center justify-center shrink-0`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{c.label}</div>
                <div className={`text-[11px] ${settings[c.key] ? "text-emerald-700" : "text-gray-500"}`}>{settings[c.key] ? "On — alerts are sent" : "Off — nothing is sent"}</div>
              </div>
              <Switch checked={!!settings[c.key]} onCheckedChange={(v) => setSettings({ ...settings, [c.key]: v })} data-testid={`toggle-${c.key}`} />
            </div>
          );
        })}
        <div className="glass rounded-lg px-3 py-3 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${summary.waCritical ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
            {summary.waCritical ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-sm">{summary.waCritical} on WhatsApp for critical</div>
            <div className="text-[11px] text-gray-500">{summary.active} active contact{summary.active === 1 ? "" : "s"}</div>
          </div>
        </div>
      </div>

      {/* Problems that stop alerts reaching people */}
      {summary.issues.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="dispatch-issues">
          <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> Check these contacts</div>
          <ul className="text-xs text-amber-900 space-y-0.5 list-disc pl-5">
            {summary.issues.map((it, k) => <li key={k}><b>{it.name}:</b> {it.msg}</li>)}
          </ul>
        </div>
      )}

      {/* Contacts */}
      <div className="flex items-center justify-between">
        <h2 className="font-bold">Alert contacts{" "}
          <span className="text-gray-400 font-normal text-sm">
            {viewSite && !showAll ? `· for ${siteName(viewSite)}: its own contacts plus "All sites" ones` : "· everyone in this organisation"}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          {viewSite && (showAll || hidden > 0) && (
            <Button size="sm" variant="outline" onClick={() => setShowAll(!showAll)} className="h-8 border-gray-200 text-gray-700" data-testid="show-all-contacts">
              {showAll ? `Only ${siteName(viewSite)}` : `Show everyone (${hidden} more)`}
            </Button>
          )}
          <Button size="sm" onClick={addContact} className="f360-gradient border-0 text-white" data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Contact
          </Button>
        </div>
      </div>

      {settings.contacts.length === 0 ? (
        <div className="glass rounded-lg p-8 text-center text-sm text-gray-500">No contacts yet — nobody receives alerts. Click <b>Add Contact</b>.</div>
      ) : listed.length === 0 ? (
        <div className="glass rounded-lg p-8 text-center text-sm text-gray-500">No contact covers {siteName(viewSite)} yet — nobody there receives WhatsApp or email alerts. Click <b>Add Contact</b>.</div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))" }} data-testid="dispatch-contacts">
          {listed.map(({ c, idx }) => {
            const issues = contactIssues(c);
            const isSaved = savedIds.has(c.contact_id) && !dirty;
            const legacy = (c.severities || []).filter((s) => LEGACY_SEVERITIES.includes(s));
            return (
              <div key={c.contact_id} className={`glass rounded-lg p-3 border ${!c.is_active ? "opacity-60 border-transparent" : issues.length ? "border-amber-300" : "border-transparent"}`} data-testid={`contact-row-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><UserRound className="w-4 h-4" /></div>
                  <Input className="bg-gray-50 border-gray-200 h-8 flex-1 font-semibold" value={c.name} onChange={(e) => updateContact(idx, { name: e.target.value })} placeholder="Full name" />
                  <label className="flex items-center gap-1.5 text-[11px] text-gray-600 shrink-0" title="Inactive contacts receive nothing">
                    <Switch checked={!!c.is_active} onCheckedChange={(v) => updateContact(idx, { is_active: v })} data-testid={`active-${idx}`} /> Active
                  </label>
                  <button onClick={() => deleteContact(idx)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" title="Remove contact" data-testid={`del-${idx}`}><Trash2 className="w-4 h-4" /></button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> WhatsApp number (alerts go here)</span>
                    <Input className="bg-gray-50 border-gray-200 h-8 mt-0.5" value={c.whatsapp || ""} onChange={(e) => updateContact(idx, { whatsapp: e.target.value })} placeholder="+91 98XXX XXXXX — with country code" data-testid={`wa-${idx}`} />
                  </label>
                  <label>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>
                    <Input className="bg-gray-50 border-gray-200 h-8 mt-0.5" value={c.email || ""} onChange={(e) => updateContact(idx, { email: e.target.value })} placeholder="name@company.com" />
                  </label>
                  <label>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Role</span>
                    <RoleField value={c.role || ""} onChange={(role) => updateContact(idx, { role })} />
                  </label>
                  <label className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone (reference only — no alerts are sent here)</span>
                    <Input className="bg-gray-50 border-gray-200 h-8 mt-0.5" value={c.phone || ""} onChange={(e) => updateContact(idx, { phone: e.target.value })} placeholder="Optional" />
                  </label>
                </div>

                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> Sites (alerts from)</div>
                  <div className="flex flex-wrap items-center gap-1.5" data-testid={`sites-${idx}`}>
                    {(c.site_ids || []).length === 0 ? (
                      <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border bg-gray-800 text-white border-gray-800">All sites</span>
                    ) : c.site_ids.map((sid) => (
                      <span key={sid} className="text-[11px] font-semibold pl-2.5 pr-1 py-0.5 rounded-full border bg-orange-50 text-orange-800 border-orange-200 inline-flex items-center gap-1">
                        {siteName(sid)}
                        {/* The last site can't be removed here: that would silently widen to "All sites". */}
                        {c.site_ids.length > 1 && (
                          <button type="button" onClick={() => updateContact(idx, { site_ids: c.site_ids.filter((x) => x !== sid) })} className="hover:text-red-600" title="Remove this site"><X className="w-3 h-3" /></button>
                        )}
                      </span>
                    ))}
                    {(sites || []).length > 1 && (
                      <Select value="" onValueChange={(v) => updateContact(idx, { site_ids: v === "__all__" ? [] : [...(c.site_ids || []), v] })}>
                        <SelectTrigger className="h-6 w-auto px-2 gap-1 text-[11px] border-dashed border-gray-300 bg-white text-gray-600"><SelectValue placeholder={(c.site_ids || []).length ? "+ Site" : "Only some sites…"} /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          {(c.site_ids || []).length > 0 && <SelectItem value="__all__">All sites</SelectItem>}
                          {(sites || []).filter((s) => !(c.site_ids || []).includes(s.site_id)).map((s) => (
                            <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Receives</div>
                  <div className="flex flex-wrap gap-1.5">
                    {SEVERITIES.map((s) => {
                      const on = (c.severities || []).includes(s.key);
                      return (
                        <button key={s.key} type="button" title={s.hint} onClick={() => toggleSeverity(idx, s.key)}
                          className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition ${on ? s.on : "bg-white text-gray-500 border-gray-300 hover:border-gray-400"}`}>
                          {on ? "✓ " : ""}{s.label}
                        </button>
                      );
                    })}
                    {legacy.map((s) => (
                      <button key={s} type="button" onClick={() => toggleSeverity(idx, s)} title="No alert is ever raised with this level — click to remove"
                        className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-gray-300 text-gray-400 line-through">{s}</button>
                    ))}
                  </div>
                </div>

                {issues.length > 0 && (
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">{issues[0]}</div>
                )}

                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Send test</span>
                  <Button size="sm" variant="outline" disabled={!isSaved || !digits(c.whatsapp)} onClick={() => testAlert(c, "whatsapp")}
                    className="h-7 px-2 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`test-wa-${idx}`}>
                    <Send className="w-3 h-3 mr-1" /> WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" disabled={!isSaved || !(c.email || "").trim()} onClick={() => testAlert(c, "email")}
                    className="h-7 px-2 text-xs border-gray-300 text-gray-700 hover:bg-gray-50">
                    <Send className="w-3 h-3 mr-1" /> Email
                  </Button>
                  {!isSaved && <span className="text-[10px] text-gray-400">Save first to test</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent test sends */}
      {log.length > 0 && (
        <div className="glass rounded-lg p-3">
          <div className="text-sm font-semibold flex items-center gap-1.5 mb-1.5"><History className="w-4 h-4 text-gray-500" /> Recent test sends</div>
          <div className="divide-y divide-gray-100 text-xs">
            {log.slice(0, 6).map((l) => (
              <div key={l.log_id} className="py-1 flex items-center gap-2">
                <span className="text-gray-500 w-36 shrink-0">{new Date(l.created_at).toLocaleString("en-IN")}</span>
                <span className="font-medium truncate">{l.contact_name}</span>
                <span className="text-gray-500">{l.channel === "whatsapp" ? "WhatsApp" : l.channel}</span>
                <span className={`ml-auto font-semibold ${l.status === "sent" ? "text-emerald-700" : "text-amber-700"}`}>{l.status === "sent" ? "Accepted" : "Not sent"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Advanced: templates + escalation */}
      <div className="glass rounded-lg">
        <button onClick={() => setShowAdvanced(!showAdvanced)} className="w-full flex items-center justify-between px-4 py-3">
          <span className="font-semibold text-sm flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-orange-500" /> Advanced — message templates & escalation</span>
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showAdvanced && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-3">
            <div>
              <div className="text-sm font-semibold mb-1">Message templates</div>
              <p className="text-xs text-gray-500 mb-2">
                Used for gateway, automation and other alerts. Hydrant, sprinkler, tank and jockey-pump alerts use fixed short wording.
                Variables: <code className="bg-gray-100 px-1 rounded">{"{{site_name}}"}</code> <code className="bg-gray-100 px-1 rounded">{"{{alarm_type}}"}</code> <code className="bg-gray-100 px-1 rounded">{"{{location}}"}</code> <code className="bg-gray-100 px-1 rounded">{"{{time}}"}</code> <code className="bg-gray-100 px-1 rounded">{"{{pressure_value}}"}</code>
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {Object.entries(settings.templates || {}).map(([sev, txt]) => (
                  <div key={sev}>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">{sev}</div>
                    <Textarea rows={3} className="bg-gray-50 border-gray-200 text-xs" value={txt}
                      onChange={(e) => setSettings({ ...settings, templates: { ...settings.templates, [sev]: e.target.value } })}
                      data-testid={`template-${sev}`} />
                  </div>
                ))}
              </div>
            </div>
            <div data-testid="escalation-matrix">
              <div className="text-sm font-semibold mb-1 flex items-center gap-2">
                <ArrowDown className="w-4 h-4 text-gray-500" /> Escalation
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">Not active yet</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Saved for later — alerts are currently sent once to every matching contact; nothing re-sends if an alert isn't acknowledged.</p>
              <div className="space-y-2">
                {(settings.escalation || []).map((lv, i) => (
                  <div key={lv.level} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                    <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-sm shrink-0">{lv.level}</div>
                    <label className="flex-1">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">After (minutes)</span>
                      <Input type="number" className="bg-white border-gray-200 h-8" value={lv.delay_minutes}
                        onChange={(e) => { const copy = [...settings.escalation]; copy[i] = { ...lv, delay_minutes: Number(e.target.value) }; setSettings({ ...settings, escalation: copy }); }} />
                    </label>
                    <label className="flex-1">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Notify role</span>
                      <Input className="bg-white border-gray-200 h-8" value={lv.role}
                        onChange={(e) => { const copy = [...settings.escalation]; copy[i] = { ...lv, role: e.target.value }; setSettings({ ...settings, escalation: copy }); }} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
